/**
 * 今夜の睡眠の計画まわりの部品（「今日」タブで使う）
 *
 * カレンダーの予定（朝の電車・始業、夜の飲み会など）に合わせた就寝・起床、この先の各晩、
 * 前倒しのコツや避けたいこと、計画の設定。
 */

import { AlertTriangle, BedDouble, CalendarClock, CalendarDays, ChevronDown, type LucideIcon } from 'lucide-react'
import { useState } from 'react'

import type { SleepPlanResponse, SleepPlanWindow, SleepSettings } from '@/api/types/sleep'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useSleepStore } from '@/store/useSleepStore'

import { formatJstDate, formatJstTime, formatMinutes } from '../format'
import { DayStyleSettingsSection } from './day-style-settings'

const BUFFER_OPTIONS = [30, 45, 60, 90, 120]
const WIND_DOWN_OPTIONS = [30, 45, 60, 90]
// これ以上ずれたら「早め/遅め」を出す
const SHIFT_BADGE_MIN = 15

const ShiftBadge = ({ shiftMin }: { shiftMin: number }) => {
  if (Math.abs(shiftMin) < SHIFT_BADGE_MIN) return null
  const early = shiftMin < 0
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
        early
          ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
      )}
    >
      {formatMinutes(Math.abs(shiftMin))}
      {early ? '早め' : '遅め'}
    </span>
  )
}

/** タイムラインの最後に置く今夜の就寝・起床 */
export const TonightItem = ({ plan, now }: { plan: SleepPlanResponse; now: number }) => {
  const w = plan.window
  if (!w || parseUtcDate(w.wake_at).getTime() <= now) return null
  const asleep = parseUtcDate(w.bedtime_at).getTime() <= now
  return (
    <li className="relative pl-4">
      <span className="absolute top-1.5 -left-[5px] size-2.5 rounded-full border-2 border-background bg-indigo-600" />
      <div className="space-y-1.5 rounded-lg bg-indigo-50 p-3 dark:bg-indigo-950/40">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <BedDouble className="size-4 text-indigo-600 dark:text-indigo-300" />
          <span className="text-lg font-bold tabular-nums">
            {formatJstTime(w.bedtime_at)} → {formatJstTime(w.wake_at)}
          </span>
          <span className={cn('text-sm tabular-nums', w.warning ? 'text-rose-600' : 'text-muted-foreground')}>
            {formatMinutes(w.sleep_min)}
          </span>
          <ShiftBadge shiftMin={w.shift_min} />
          {asleep && <span className="text-[11px] text-indigo-600 dark:text-indigo-300">睡眠中</span>}
        </div>
        <p className="text-xs text-muted-foreground">
          就寝: {w.bed_reason}
          <br />
          起床: {w.wake_reason}（予定が無ければ {formatMinutes(w.target_min)} 取りたい）
        </p>
        {w.warning && (
          <p className="flex items-start gap-1.5 text-sm text-rose-700 dark:text-rose-300">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            {w.warning}
          </p>
        )}
        {w.events.length > 0 && (
          <ul className="space-y-0.5 text-xs text-muted-foreground">
            {w.events.map((e) => (
              <li key={`${e.role}-${e.start_at}`} className="flex items-center gap-1.5">
                <CalendarClock className="size-3.5 shrink-0" />
                {e.role === 'wake' ? '起きる基準' : '寝る基準'}: {formatJstDate(e.start_at)} {formatJstTime(e.start_at)}
                〜{formatJstTime(e.end_at)}「{e.title}」
              </li>
            ))}
          </ul>
        )}
        {plan.calendar_error && (
          <p className="text-xs text-amber-600">カレンダーを読めなかったため、予定を考慮していません</p>
        )}
      </div>
    </li>
  )
}

const NightRow = ({ night }: { night: SleepPlanWindow }) => {
  const wakeEvent = night.events.some((e) => e.role === 'wake')
  return (
    <li className="space-y-0.5 py-2">
      <div className="flex items-center gap-2 text-sm">
        <span className="w-20 shrink-0 text-xs text-muted-foreground">{formatJstDate(night.wake_at)}の朝</span>
        <span className="font-semibold tabular-nums">
          {formatJstTime(night.bedtime_at)}→{formatJstTime(night.wake_at)}
        </span>
        <span className={cn('text-xs tabular-nums', night.warning ? 'text-rose-600' : 'text-muted-foreground')}>
          {formatMinutes(night.sleep_min)}
        </span>
        <span className="ml-auto">
          <ShiftBadge shiftMin={night.shift_min} />
        </span>
      </div>
      <p className="pl-22 text-xs text-muted-foreground">
        {wakeEvent ? `${night.wake_reason}に起きる。` : ''}
        {night.bed_reason}
      </p>
    </li>
  )
}

/** この先の各晩（たたんでおき、押すと開く） */
export const UpcomingNights = ({ nights }: { nights: SleepPlanWindow[] }) => {
  const [open, setOpen] = useState(false)
  if (nights.length <= 1) return null
  return (
    <section className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-sm font-semibold"
      >
        <CalendarDays className="size-4" />
        これからの睡眠予定
        <span className="text-xs font-normal text-muted-foreground">（{nights.length}晩）</span>
        <ChevronDown className={cn('ml-auto size-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <>
          <p className="text-xs text-muted-foreground">
            早起きの日があるときは、体内時計を急にずらせないので前の晩から1日1時間ずつ前倒しし、終わったら少しずつ戻します
          </p>
          <ul className="divide-y">
            {nights.map((night) => (
              <NightRow key={night.bedtime_at} night={night} />
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

export const TextList = ({ title, icon: Icon, items }: { title: string; icon: LucideIcon; items: string[] }) => {
  if (items.length === 0) return null
  return (
    <section className="space-y-1.5">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <Icon className="size-4" />
        {title}
      </h3>
      <ul className="list-disc space-y-1 pl-5 text-sm">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  )
}

const SettingSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: number
  options: number[]
  onChange: (value: number) => void
}) => {
  const values = options.includes(value) ? options : [...options, value].sort((a, b) => a - b)
  return (
    <label className="flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {values.map((v) => (
            <SelectItem key={v} value={String(v)}>
              {formatMinutes(v)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  )
}

export const SleepSettingsSheet = ({
  settings,
  open,
  onOpenChange,
}: {
  settings: SleepSettings
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const saveSettings = useSleepStore((s) => s.saveSettings)
  return (
    <BottomSheet title="睡眠の計画の設定" open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
      <div className="space-y-3 px-4 pb-6">
        <SettingSelect
          label="起きてから朝の予定まで"
          value={settings.morning_buffer_min}
          options={BUFFER_OPTIONS}
          onChange={(v) => void saveSettings({ ...settings, morning_buffer_min: v })}
        />
        <SettingSelect
          label="寝る準備を始めるのは就寝の"
          value={settings.wind_down_min}
          options={WIND_DOWN_OPTIONS}
          onChange={(v) => void saveSettings({ ...settings, wind_down_min: v })}
        />
        <p className="text-xs text-muted-foreground">
          朝の予定の開始時刻から「起きてから朝の予定まで」を引いた時刻に起きる計画になります（移動時間も含めて設定してください）
        </p>
        <p className="text-xs text-muted-foreground">
          寝る準備の通知は、タイムラインの各項目のベルでオン・オフできます
        </p>
        <DayStyleSettingsSection
          value={settings.day_style}
          onSave={(day_style) => saveSettings({ ...settings, day_style })}
        />
      </div>
    </BottomSheet>
  )
}
